import { gameLabel, type GameType } from '@tv-casino/shared';

/**
 * The title card a screen throws up when the felt changes game. A switch is
 * the one moment where every picture on every screen is replaced at once —
 * the wheel goes, the dice go, the phone's whole control strip is swapped
 * under a thumb — so the room gets told what happened instead of watching the
 * table blink into something else.
 *
 * One model for both screens: the TV shows the big card, the phone shows the
 * same words small, and neither can name the new game differently.
 */
export interface GameSplashVM {
  game: GameType;
  /** "Roulette" — the new felt, as every picker spells it. */
  title: string;
  /** "Now dealing", or "Blackjack → Roulette" when the old game is known. */
  kicker: string;
  /** What the game is, in one line. */
  tagline: string;
  /** What the player does next on their phone. */
  hint: string;
  /** The table's own rule line, when the caller has one to hand over. */
  rule: string | null;
}

/** How long the TV holds the card before the new felt takes the screen. */
export const GAME_SPLASH_MS = 2600;

/** The phone's is shorter: it is a label on a swap, not a curtain. */
export const PHONE_SPLASH_MS = 1800;

interface SplashCopy {
  tagline: string;
  hint: string;
}

const COPY: Record<GameType, SplashCopy> = {
  blackjack: {
    tagline: 'Beat the dealer to 21 without going over.',
    hint: 'Bet on your phone, then hit or stand.',
  },
  holdem: {
    tagline: 'Two cards each, five on the board, best five play.',
    hint: 'Blinds post themselves. Act when your phone lights up.',
  },
  baccarat: {
    tagline: 'Back player, banker or the tie.',
    hint: 'Pick a spot on your phone before the window shuts.',
  },
  roulette: {
    tagline: 'One ball, one pocket, the whole layout to back.',
    hint: 'Lay chips on your phone, then watch the wheel.',
  },
  craps: {
    tagline: 'The shooter rolls for the whole table.',
    hint: 'Pass line first — odds once the point is on.',
  },
  threecard: {
    tagline: 'Three cards against the dealer, queen-high plays.',
    hint: 'Ante on your phone, then play or fold.',
  },
  keno: {
    tagline: 'Pick your spots, the house draws twenty.',
    hint: 'Mark a ticket on your phone and lock it in.',
  },
};

export interface GameSplashOptions {
  /** The felt being left, when the screen saw it. Names the move in the kicker. */
  from?: GameType | null;
  /** The table's rule line, so the card carries the limits it is switching to. */
  rule?: string | null;
}

/** The switch card for one game, as a screen should paint it. */
export function gameSplashVM(game: GameType, opts: GameSplashOptions = {}): GameSplashVM {
  const { from = null, rule = null } = opts;
  const copy = COPY[game];
  return {
    game,
    title: gameLabel(game),
    kicker: from && from !== game ? `${gameLabel(from)} → ${gameLabel(game)}` : 'Now dealing',
    tagline: copy.tagline,
    hint: copy.hint,
    rule: rule && rule.trim() !== '' ? rule : null,
  };
}
